"use client"

import { ProductCategory } from "@medusajs/medusa"
import { Heading } from "@medusajs/ui"

import { useClientSessionId } from "@hooks/useClientSessionId"
import { retrieveUTMParams } from "@lib/util/utm-params"
import ExternalLink from "@modules/common/components/external-link"

import HeroHelper from "./HeroHelper"

type HeroProps = {
  categories: ProductCategory[]
}

function findAllWithHeroImage(categories: ProductCategory[]): ProductCategory[] {
  const results: ProductCategory[] = []

  for (const cat of categories) {
    if (cat.metadata?.hero_image) {
      results.push(cat)
    }
    if (cat.category_children && cat.category_children.length > 0) {
      const childMatches = findAllWithHeroImage(cat.category_children)
      results.push(...childMatches)
    }
  }

  return results
}

const Hero = ({ categories }: HeroProps) => {
  const categoriesWithHeroImage = findAllWithHeroImage(categories)
  const { sessionId, isMounted } = useClientSessionId()

  return (
    <div className="mx-4 mb-6 max-h-[350px] max-w-screen-md md:mx-0 md:flex md:max-w-none md:flex-row md:items-stretch md:gap-7">
      <section className="content-container relative flex w-full items-center rounded-[20px] bg-hero-gradient bg-cover md:bg-center md:pl-10">
        <div className="sm:pb-0md:max-w-[60vw] z-10 flex flex-col justify-center py-4 text-left sm:pt-0 md:pb-0">
          <Heading level="h1" className="mb-0 font-manrope text-[32px] font-light text-white md:mt-2 md:text-[48px] md:leading-[56px]">
            <span>
              Compra en <br />
              <span className="font-medium">Tienda Nelo</span>
            </span>
          </Heading>
          <Heading level="h2" className="mt-1 font-manrope text-xl font-light leading-8 text-white md:mt-2 md:text-2xl md:leading-10">
            <span className="block text-base md:hidden">A quincenas, sin intereses</span>
            <span className="hidden text-lg md:block">
              Descubre miles de productos <br />y compra a quincenas, sin intereses
            </span>
          </Heading>
          <div className="mb-4 mt-6 md:mt-8">
            {isMounted && (
              <ExternalLink
                href={`${process.env.NEXT_PUBLIC_SIGNUP_ORIGIN}/signup/phone-email?${new URLSearchParams({
                  source: "marketplace",
                  storefrontSessionId: sessionId,
                  ...retrieveUTMParams(),
                }).toString()}`}
              >
                <button className="rounded-full border-none bg-white px-3 py-2 text-center font-manrope text-xs font-bold text-nelo-blue transition-opacity duration-300 hover:bg-nelo-citron focus:shadow-[inset_0_2px_4px_rgba(0,0,0,0.1)] md:px-6 md:py-3 md:text-base">
                  Crea tu cuenta
                </button>
              </ExternalLink>
            )}
          </div>
        </div>
      </section>
      <section className="hidden aspect-square w-2/5 overflow-hidden rounded-[20px] lg:block">
        <HeroHelper categories={categoriesWithHeroImage} autoScrollDuration={4000} />
      </section>
    </div>
  )
}

export default Hero
